import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Logo } from "../../components/shared/Logo";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center noise-cc">
    <Logo size={36} className="text-foreground mb-8" />
    {children}
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 10) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { setStatus("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <Shell>
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-12 h-12 animate-spin text-[var(--cc-accent)] mx-auto" />
          <h1 className="font-heading font-bold text-2xl mt-6">Confirming payment...</h1>
          <p className="text-muted-foreground mt-2 text-sm">Verifying with Stripe. Do not close this page.</p>
        </div>
      )}
      {status === "paid" && (
        <div className="fade-up" data-testid="payment-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h1 className="font-heading font-bold text-3xl mt-6">Payment confirmed.</h1>
          <p className="text-muted-foreground mt-2">You're all set — a full record lives in your portal.</p>
          <Link to="/portal"><Button data-testid="payment-success-portal-button" className="mt-8 rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">Back to My Portal</Button></Link>
        </div>
      )}
      {(status === "error" || status === "timeout") && (
        <div data-testid="payment-error">
          <XCircle className="w-14 h-14 text-red-500 mx-auto" />
          <h1 className="font-heading font-bold text-3xl mt-6">{status === "timeout" ? "Still processing" : "Payment issue"}</h1>
          <p className="text-muted-foreground mt-2 max-w-md">{status === "timeout" ? "Payment is taking longer than expected. Check your invoices in a moment — we'll update it automatically." : "We couldn't confirm this payment. Your card was not charged if checkout didn't complete."}</p>
          <Link to="/portal/invoices"><Button variant="outline" className="mt-8 rounded-full">Back to Invoices</Button></Link>
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div data-testid="payment-cancelled">
        <XCircle className="w-14 h-14 text-amber-500 mx-auto" />
        <h1 className="font-heading font-bold text-3xl mt-6">Checkout cancelled.</h1>
        <p className="text-muted-foreground mt-2">No charge was made. Your invoice remains open.</p>
        <Link to="/portal/invoices"><Button className="mt-8 rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">Back to Invoices</Button></Link>
      </div>
    </Shell>
  );
}
